/* eslint-disable max-classes-per-file */

/* eslint-disable class-methods-use-this */
import { Pagination } from '@packages/toolbox/dist/declarations/pagination';
import { AxiosPromise, AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';

import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import { REPORTS, reportsUrlBasic } from 'SharedDir/backend/endpoints/reports-kind';
import { AttachmentSigned, AttachmentType } from 'SharedDir/declarations/files';
import {
  ReportConsolidationGrid,
  ReportDateList,
  ReportForm,
  ReportKind,
  ReportKindList,
} from 'SharedDir/declarations/reports';

import { paramsAllRequest } from '../default-params';

// import { Catalogs } from 'SharedDir/declarations/catalogs';
// import { CatalogValueListOptions } from 'SharedDir/backend/api/classes/CatalogsApiClass';

type ServiceGroupResponse = Pagination.PaginationAndFilterParams<{
  data: ReportKindList;
}>;

export default class ReportsKindApiClass extends SimpleApi<ReportKind, ReportKind> {
  constructor() {
    super(reportsUrlBasic);
  }

  async getReportFormsList(reportKindId) {
    const endpoint = { url: `${this.url}/${reportKindId}/forms`, method: 'get' };
    const { data } = (await http({
      ...endpoint,
    })) as AxiosResponse<ReportForm>;
    return data;
  }

  async getReportForm(reportKindId, formId) {
    const endpoint = { url: `${this.url}/${reportKindId}/forms/${formId}`, method: 'get' };
    const { data } = (await http({
      ...endpoint,
    })) as AxiosResponse<ReportForm>;
    return data;
  }

  async getReportingDates(reportKindId, params) {
    const endpoint = { url: `${this.url}/${reportKindId}/reporting-dates`, method: 'get' };
    const { data } = (await http({
      ...endpoint,
      params,
    })) as AxiosResponse<ReportDateList>;
    return data;
  }

  public ReportsApi(reportKindId) {
    return new (class extends SimpleApi<ReportForm, ReportForm> {
      async fillReportForm(reportId, saveData) {
        const endpoint = {
          url: `${this.url}/${reportId}/fill`,
          method: 'PUT',
        };
        const { data } = (await http({
          ...endpoint,
          data: saveData,
        })) as AxiosResponse<ReportForm>;
        return data;
      }

      async fillIntegrationReportForm(reportId) {
        const endpoint = {
          url: `${this.url}/${reportId}/fill-integration`,
          method: 'PUT',
        };
        const { data } = (await http({
          ...endpoint,
        })) as AxiosResponse<ReportForm>;
        return data;
      }

      async copyReportForm(reportId, params) {
        const endpoint = {
          url: `${this.url}/${reportId}/copy`,
          method: 'POST',
          data: params,
        };
        const { data } = (await http({
          ...endpoint,
        })) as AxiosResponse<ReportForm>;
        return data;
      }

      async getHistoryList(reportId, reportDate, params) {
        const endpoint = {
          url: `${this.url}/${reportId}/history`,
          method: 'GET',
          params: { reportDate, ...params },
        };
        const { data } = (await http({
          ...endpoint,
        })) as AxiosResponse<any>;
        return data;
      }

      async getHistory(reportId, versionId, reportDate) {
        const endpoint = {
          url: `${this.url}/${reportId}/history/${versionId}`,
          method: 'GET',
          params: { reportDate },
        };
        const { data } = (await http({
          ...endpoint,
        })) as AxiosResponse<any>;
        return data;
      }

      async getStatusHistoryList(reportId, params) {
        const endpoint = {
          url: `${this.url}/${reportId}/status-history`,
          method: 'GET',
          params,
        };
        const { data } = (await http({
          ...endpoint,
        })) as AxiosResponse<any>;
        return data;
      }

      async updateStatus(reportId, saveData) {
        const endpoint = {
          url: `${this.url}/${reportId}/status`,
          method: 'PUT',
        };
        const { data } = (await http({
          ...endpoint,
          data: saveData,
        })) as AxiosResponse<ReportForm>;
        return data;
      }

      async consolidateReports(payload: {
        periods: Array<{ reportDate: string; periodId: number }>;
      }): Promise<ReportConsolidationGrid> {
        const { data } = (await http({
          url: `${this.url}/consolidation`,
          method: 'POST',
          data: payload,
        })) as AxiosResponse<ReportConsolidationGrid>;
        return data;
      }

      async exportConsolidation(
        payload: {
          periods: Array<{ reportDate: string; periodId: number }>;
        },
        fileFormat: 'xlsx' | 'pdf' | 'xml' = 'xlsx'
      ): Promise<AxiosResponse> {
        return http({
          url: `${this.url}/consolidation/export`,
          method: 'POST',
          data: payload,
          params: { format: fileFormat },
          responseType: 'blob',
        }) as AxiosPromise<AxiosResponse>;
      }

      async exportReports(
        reportId,
        date,
        fileFormat: 'xlsx' | 'pdf' | 'xml' = 'xlsx'
      ): Promise<AxiosResponse> {
        const endpoint = {
          url: `${this.url}/${reportId}/export`,
          method: 'POST',
          params: { format: fileFormat },
        };
        return http({
          ...endpoint,
          data: date,
          responseType: 'blob',
        }) as AxiosPromise<AxiosResponse>;
      }

      async downloadReportTemplate(reportId, fileFormat: 'xlsx' | 'csv'): Promise<AxiosResponse> {
        return http({
          url: `${this.url}/${reportId}/template`,
          method: 'GET',
          params: { format: fileFormat },
          responseType: 'blob',
        }) as AxiosPromise<AxiosResponse>;
      }

      async setAttachment(
        reportId: number,
        payload: { fileId: string | null }
      ): Promise<{ attachmentFile: AttachmentType | null }> {
        const { data } = (await http({
          url: `${this.url}/${reportId}/attachment`,
          method: 'PUT',
          data: payload,
        })) as AxiosResponse<{ attachmentFile: AttachmentType | null }>;
        return data;
      }

      async freezeSignFile(reportId: number): Promise<AttachmentSigned> {
        const { data } = (await http({
          ...REPORTS.REPORTS.SIGN_FILE(this.url, reportId),
        })) as AxiosResponse<AttachmentSigned>;
        return data;
      }

      async importReportTemplate(reportId, file: File): Promise<AxiosResponse> {
        const formData = new FormData();
        formData.append('file', file);
        return http({
          url: `${this.url}/${reportId}/import`,
          method: 'POST',
          data: formData,
        }) as AxiosPromise<AxiosResponse>;
      }
    })(`${this.url}/${reportKindId}/reports`);
  }

  // public static async getPublicList(): Promise<ServiceGroupResponse> {
  //   const { data }: AxiosResponse<ServiceGroupResponse> = await http({
  //     ...SERVICE_GROUPS.SERVICE_GROUPS.PUBLIC_LIST,
  //     params: paramsAllRequest,
  //   });
  //   return data;
  // }
}
